/*
 * Opted out of the React Compiler, and it has to be every form in the panel
 * rather than the ones that happen to break.
 *
 * `register()` returns a fresh `ref` callback on every render, and react-hook-form
 * depends on it: React detaches the old one and attaches the new, which is when
 * the library writes a value back into an uncontrolled input. The compiler
 * memoises the spread, the callback identity stops changing, React stops
 * calling it — and `reset(values)` silently leaves the DOM as the reader typed
 * it. Measured: discard restored nothing, and the next edit no longer raised
 * the unsaved-changes guard.
 *
 * The compiler already declines several of these on its own — that is the
 * `react-hooks/incompatible-library` warning — but which ones it declines is
 * not something to depend on.
 */
'use no memo'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { SESSION_QUERY_KEY } from '@/shared/config'
import { Alert, AlertDescription, Button, FloatingField } from '@/shared/ui'

import { authSettingsQuery, minPasswordLengthOf } from '../api/settings-api'
import type { RegistrationFailure } from '../api/token-api'
import { register as registerAccount } from '../model/session-lifecycle'
import { checkUsername, normalizeUsername } from '../model/username'

const MESSAGE_KEYS: Record<RegistrationFailure, string> = {
  taken: 'register.taken',
  invalid: 'register.invalid',
  closed: 'register.closed',
  rate_limited: 'register.rateLimited',
  unavailable: 'register.unavailable',
}

/**
 * The password floor is the provider's, so the schema is built once the
 * settings query answers rather than hardcoding a number that can drift.
 */
const schemaFor = (minPasswordLength: number) =>
  z.object({
    username: z.string().superRefine((value, ctx) => {
      const problem = checkUsername(value)
      if (problem === null) return
      ctx.addIssue({
        code: 'custom',
        message: problem === 'reserved' ? 'register.usernameReserved' : 'register.usernamePattern',
      })
    }),
    password: z.string().min(minPasswordLength, { message: 'common:validation.min' }),
  })

type RegisterValues = { username: string; password: string }

export function RegisterForm({ returnTo }: { returnTo: string }) {
  const { t } = useTranslation(['auth', 'common'])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: settings } = useQuery(authSettingsQuery())
  const minPasswordLength = minPasswordLengthOf(settings)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RegisterValues>({
    resolver: zodResolver(schemaFor(minPasswordLength)),
    defaultValues: { username: '', password: '' },
    // Validating on blur paints a field red the moment focus leaves it, so
    // tabbing through the form is enough to light it up before anything has
    // been typed. Complain on submit, then correct live as the user fixes it.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const onSubmit = handleSubmit(async (values) => {
    // Lowercased here as well as on the server: what the user sees signed in as
    // should be what they typed into the field, not a surprise.
    const result = await registerAccount(normalizeUsername(values.username), values.password)

    if (!result.ok) {
      setError('root', { message: t(MESSAGE_KEYS[result.reason]) })
      return
    }

    // The guard cached `null` before this form was shown; left in place, the
    // navigation below would read it and bounce straight back.
    queryClient.removeQueries({ queryKey: SESSION_QUERY_KEY })
    await navigate({ href: returnTo, replace: true })
  })

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4" noValidate>
      {errors.root !== undefined && (
        <Alert variant="destructive">
          <AlertDescription>{errors.root.message}</AlertDescription>
        </Alert>
      )}

      <FloatingField
        label={t('register.username')}
        autoComplete="username"
        autoFocus
        tone={errors.username === undefined ? 'normal' : 'error'}
        hint={
          errors.username === undefined
            ? t('register.usernameHint')
            : t(errors.username.message ?? 'register.usernamePattern')
        }
        {...register('username')}
      />

      <FloatingField
        label={t('register.password')}
        type="password"
        autoComplete="new-password"
        tone={errors.password === undefined ? 'normal' : 'error'}
        // The rule is the same sentence whether it is advice or a complaint.
        hint={t('common:validation.min', { count: minPasswordLength })}
        {...register('password')}
      />

      <Button type="submit" variant="default" isLoading={isSubmitting}>
        {t('register.submit')}
      </Button>
    </form>
  )
}
